/**
 * CortexAnimatedLogo — Features Soumili's 3-photon rail animation on cortex_mark.png,
 * seamlessly integrated into Debasmit's theme-aware design system.
 */

import cortexMark from '@/assets/cortex_mark.png';

interface CortexAnimatedLogoProps {
  /** Size in px (default 100) */
  size?: number;
  className?: string;
}

export function CortexAnimatedLogo({ size = 100, className }: CortexAnimatedLogoProps) {
  return (
    <div
      className={`relative flex items-center justify-center ${className || ''}`}
      style={{ width: size, height: size }}
    >
      {/* Soft ambient background aura */}
      <div 
        className="absolute inset-0 rounded-full animate-cortex-aura pointer-events-none"
        style={{
          background: 'radial-gradient(circle at 50% 50%, rgba(106, 174, 224, 0.35) 0%, rgba(26, 79, 140, 0.12) 55%, transparent 75%)',
          filter: 'blur(12px)',
          transform: 'scale(1.3)'
        }}
      />

      {/* Cortex Mark PNG asset */}
      <img
        src={cortexMark}
        alt="Cortex Mark"
        draggable={false}
        className="block w-full h-full object-contain select-none z-10"
      />

      {/* SVG Photon Glint Overlay riding measured rails (inner triangle, outer triangle, outer circle) */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none z-20 overflow-visible"
        viewBox="0 0 300 300"
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <radialGradient id="splashGlintGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="0.95" />
            <stop offset="60%" stopColor="#38bdf8" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#38bdf8" stopOpacity="0" />
          </radialGradient>

          <path id="railInner" d="M145.37,72.74 L206.22,188.46 L84.51,188.46 Z" fill="none" />
          <path id="railOuter" d="M139.5,11.5 L251.5,211.5 L35.5,211.5 Z" fill="none" />
          <path
            id="railCircle"
            d="M143.5,11.44 A129.18,129.18 0 0 1 143.5,269.80 A129.18,129.18 0 0 1 143.5,11.44"
            fill="none"
          />
        </defs>

        {/* 1 — Inner triangle photon */}
        <g>
          <animateMotion dur="2.4s" repeatCount="indefinite" begin="0s">
            <mpath xlinkHref="#railInner" />
          </animateMotion>
          <animate
            attributeName="opacity"
            dur="2.4s"
            repeatCount="indefinite"
            begin="0s"
            values="0;1;1;1;0"
            keyTimes="0;0.06;0.5;0.94;1"
          />
          <circle r="12" fill="url(#splashGlintGlow)" />
          <circle r="3" fill="#ffffff" />
        </g>

        {/* 2 — Outer triangle photon */}
        <g>
          <animateMotion dur="2.4s" repeatCount="indefinite" begin="0.8s">
            <mpath xlinkHref="#railOuter" />
          </animateMotion>
          <animate
            attributeName="opacity"
            dur="2.4s"
            repeatCount="indefinite"
            begin="0.8s"
            values="0;1;1;1;0"
            keyTimes="0;0.06;0.5;0.94;1"
          />
          <circle r="13" fill="url(#splashGlintGlow)" />
          <circle r="3.2" fill="#ffffff" />
        </g>

        {/* 3 — Outer circle photon */}
        <g>
          <animateMotion dur="2.4s" repeatCount="indefinite" begin="1.6s">
            <mpath xlinkHref="#railCircle" />
          </animateMotion>
          <animate
            attributeName="opacity"
            dur="2.4s"
            repeatCount="indefinite"
            begin="1.6s"
            values="0;1;1;1;0"
            keyTimes="0;0.06;0.5;0.94;1"
          />
          <circle r="14" fill="url(#splashGlintGlow)" />
          <circle r="3.5" fill="#ffffff" />
        </g>
      </svg>
    </div>
  );
}
